import { Check } from 'lucide-react';
import { useState } from 'react';
import { formatDateOfBirth } from '../../utils/dateUtils';
import LoadingScreen from './LoadingScreen';

const OCRResultForm = ({ ocrData, editableOcrData, onFieldChange, onConfirm, onStartOver }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare payload for API call (will be called later with auth token)
      const payload = {
        nidNumber: editableOcrData?.nidNumber || '',
        nameEnglish: editableOcrData?.nameEnglish || '',
        nameBangla: editableOcrData?.nameBangla || '',
        dateOfBirth: formatDateOfBirth(editableOcrData?.dateOfBirth || ''),
        fatherName: editableOcrData?.fatherName || '',
        motherName: editableOcrData?.motherName || '',
        address: editableOcrData?.address || '',
        isFaceMatchRequired: false,
      };

      // Call the original onConfirm callback with the payload
      await onConfirm(payload);
    } catch (err) {
      console.error('Error preparing payload:', err);
      setError('Failed to prepare data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fields = [
    { key: 'nameEnglish', label: 'Name (English):', placeholder: 'Enter name in English' },
    { key: 'nameBangla', label: 'Name (Bangla):', placeholder: 'Enter name in Bangla' },
    { key: 'fatherName', label: "Father's Name:", placeholder: "Enter father's name" },
    { key: 'motherName', label: "Mother's Name:", placeholder: "Enter mother's name" },
    { key: 'nidNumber', label: 'NID Number:', placeholder: 'Enter NID number' },
    { key: 'dateOfBirth', label: 'Date of Birth:', placeholder: 'DD-MM-YYYY' },
  ];

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-2xl">
        <div className="card">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={40} className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">OCR Completed Successfully!</h2>
            <p className="text-gray-600">Your NID information has been extracted using Tesseract.js</p>
          </div>

          {/* OCR Results - Editable Text Boxes */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">Extracted Information (Editable)</h3>
            
            <div className="space-y-3">
              {fields.map(({ key, label, placeholder }) => (
                <div key={key} className="flex flex-col gap-1 border-b border-gray-200 pb-3">
                  <label className="text-gray-600 font-medium text-sm">{label}</label>
                  <input
                    type="text"
                    value={editableOcrData?.[key] || ''}
                    onChange={(e) => onFieldChange(key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder={placeholder}
                  />
                </div>
              ))}

              {/* Address - Textarea */}
              <div className="flex flex-col gap-1">
                <label className="text-gray-600 font-medium text-sm">Address:</label>
                <textarea
                  value={editableOcrData?.address || ''}
                  onChange={(e) => onFieldChange('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows={3}
                  placeholder="Enter full address"
                />
              </div>
            </div>
          </div>

          {/* Info Note */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This information was extracted using Tesseract.js (100% offline OCR). 
              Please review and edit if needed before proceeding.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {error}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onStartOver}
              className="btn-secondary flex-1"
              disabled={isLoading}
            >
              Start Over
            </button>
            <button
              onClick={handleConfirm}
              className="btn-primary flex-1"
              disabled={isLoading}
            >
              Confirm & Proceed to Liveness
            </button>
          </div>
        </div>
      </div>
      {/* Loading Overlay */}
      {isLoading && (
        <LoadingScreen
          message="Verifying NID information..."
          subMessage="Please wait while we verify your data"
        />
      )}
    </div>
  );
};

export default OCRResultForm;
